import find from 'lodash/find'
import first from 'lodash/first'
import includes from 'lodash/includes'
import join from 'lodash/join'
import map from 'lodash/map'
import merge from 'lodash/merge'
import noop from 'lodash/noop'
import reject from 'lodash/reject'
import toNumber from 'lodash/toNumber'
import { useCallback, useContext, useState } from 'react'
import { usePopupManager } from 'react-popup-manager'

import { AddedToCartModal } from '@Components/AddedToCartModal'
import { ErrorModal } from '@Components/ErrorModal'
import { useSizeSelectorModal } from '@Components/SizeModal/useSizeSelectorModal'
import { AddToCartLegacyProductPersonalizationInput, Product } from '@Generated/graphql'
import { useAddToCartQuery } from '@Globals/Cart/useAddToCartQuery'
import { convertToMParticle, getCrossSellsForProduct, getPersonalizationsFromProduct } from '@Globals/CMS/ProductHelpers'
import { gtm } from '@Globals/Google/DataLayer'
import { useMParticle } from '@Globals/MParticle/useMParticle'
import { UserContext } from '@Globals/User'

export type CartErrorTypes =
// Invalid product passed to endpoint - treat this as an unknown error.
  'INVALID_PRODUCT' |
  // Magento specific error when we can’t find the correct variant - treat this as an unknown error
  'LEGACY_CONFIGURABLE_ATTRIBUTE' |
  // Incorrect personalisation ID - treat this as an unknown error
  'INVALID_PERSONALIZATION_ID' |
  // When you try to add multiple engravings to a product
  'DOUBLE_ENGRAVING' |
  // When you try to add more quantity than there is in stock
  'REQUESTED_QUANTITY_NOT_IN_STOCK' |
  // This is (probably) not something that'll come back from the server, but rather our own fallback if all else fails.
  'UNKNOWN_ERROR'

export type PersonalisationReturnData = AddToCartLegacyProductPersonalizationInput | void | undefined | null

export type UseAddToCartProps = {
  additionalIDs?: string[]
  disableFlow?: boolean
  getPersonalizationData?: (() => PersonalisationReturnData) | (() => Promise<PersonalisationReturnData>)
  isCrossSell?: boolean
  postProcess?: (() => void) | (() => Promise<void>) | typeof noop
  product: Product
  quantity?: number
  sourceID?: string
  updateProduct?: boolean
  variantID?: string
}

export const useAddToCart = ({
  additionalIDs,
  disableFlow = false,
  getPersonalizationData,
  isCrossSell,
  postProcess = noop,
  product,
  quantity,
  sourceID = 'inherit',
  variantID,
  updateProduct,
}: UseAddToCartProps) => {
  const [inProgress, setInProgress] = useState(false)
  const { closeAll: closeAllPopups, open: openPopup } = usePopupManager()
  const addToCart = useAddToCartQuery()
  const askUserForSize = useSizeSelectorModal({ preselectedID: variantID, product })
  const { revalidate } = useContext(UserContext)
  const { logProductAction, otherEvent } = useMParticle()

  const callback = useCallback(
    async () => {
      // Alright, this is not a drill! We need to add stuff to the basket, but before we do, we need to ensure
      // that we have all the data we need. We do this by sort of "daisy-chaining" the needed choices as modals.
      // Each modal should come in as an asynchronous hook that we can wait for to resolve. If any modal
      // rejects, we'll take that as an "abort" signal and stop the process entirely – otherwise, this'll just
      // play out until the user has made all their choices, and we can add the product to basket.
      try {
        const personalization = (getPersonalizationData && await getPersonalizationData()) || undefined

        // Figure out which size we need.
        const variantIDToAdd = variantID || await askUserForSize()

        // We COULD set the "in progress" state further up, but if we did, the spawning button will look as if it's
        // loading already, which can confuse and anger the Common Person™. We'll just do it here, and... uh... yeah!
        setInProgress(true)

        // Close any and all popups.

        if (!updateProduct && !disableFlow) {
          closeAllPopups()
        }

        try {
          const resultData = await addToCart({
            // "Additional IDs" refer to cross sells from the product page, but could ostensibly be anything. Well, as
            // long as it's a product.
            additionalProductIDs: additionalIDs,
            personalization,
            productID: variantIDToAdd || product.id,
            // First workaround to when adding a personalization, don't double add product to cart, but only update personalization
            quantity: updateProduct ? 0 : quantity,
          })

          if (!resultData) {
            throw new Error('UNKNOWN_ERROR' as CartErrorTypes)
          }

          const { addedProductIDsWithPersonalizationType, addedProducts, failedProducts, quoteID } = resultData

          // We might have failed products. That ain't good, and if both of these are true, we're dealing with
          // a catastrophic failure. If there was at least one added product, we could consider the operation a
          // partial success, and should not handle it here.
          const hasFailedProducts = !!failedProducts?.length
          const hasPurchasePreventingError = ['DOUBLE_ENGRAVING'].includes(first(failedProducts)?.error as CartErrorTypes | '')
          const hasMultipleProducts = addedProducts?.productIDs?.length
          if ((hasFailedProducts && !hasMultipleProducts) || hasPurchasePreventingError) {
            // We'll just go with the first reason we find.
            const firstFailure = first(failedProducts)?.error as CartErrorTypes | undefined
            if (firstFailure) {
              throw new Error(firstFailure)
            } else {
              throw new Error('UNKNOWN_ERROR' as CartErrorTypes)
            }
          }

          // Reload the user data, which should update the mini basket thingie.
          await revalidate()

          // Be merry!
          if (updateProduct && !disableFlow) {
            closeAllPopups()
          }
          const data = merge(resultData, { withPersonalization: updateProduct, variantIDToAdd: variantIDToAdd || product.id })
          if (!disableFlow) {
            openPopup(AddedToCartModal, {
              data,
              onHide: (...params) => {
                if (params && !!params.length && params.length > 0) {
                  closeAllPopups()
                }
              },
              product,
            })
          }

          if (!updateProduct) {
            otherEvent({
              eventName: 'cart_add_click',
              attributes: {
                quote_id: quoteID || '',
                source_id: sourceID,
              },
            })
          }

          const categoryIDs = join(product.categoryIDs, ',')
          gtm({
            // TODO: These categoryIds should be consolidated in GTM. But current GTM variable setup is using both.
            categoryIds: categoryIDs,
            categoryContentIds: categoryIDs,
            event: 'addToCart',
            productData: {
              id: product.id,
              // @ts-ignore
              localPrice: toNumber(product.masterVariant?.price?.discount?.plain) || toNumber(product.masterVariant?.price?.plain) || 0,
            },
            productName: product.name || '',
            productId: parseInt(product.id, 10),
          })

          let personalizationType = ''
          const mParticleProducts = map(
            addedProducts?.products,
            (item) => {
              const productWithPersonalization = find(addedProductIDsWithPersonalizationType, i => i.product?.id === item.id)

              if (productWithPersonalization?.type) {
                personalizationType = productWithPersonalization.type
              }
              // TODO: fix quantity when we need to support more quantity
              return convertToMParticle(item, 1, productWithPersonalization?.type || '')
            },
          )

          const availableCrossSells = reject(
            getCrossSellsForProduct(product),
            item => includes(addedProducts?.productIDs, item.id),
          )

          if (!updateProduct) {
            logProductAction({
              custom_attributes: {
                category_ids: 'inherit',
                has_personalised_products: !!personalization,
                // TODO: just checking on additionalIDs does not mean it's crossSell.
                is_cross_sale_product_added: isCrossSell || (additionalIDs && additionalIDs.length !== 0),
                is_high_cross_sale_available: availableCrossSells.length !== 0,
                is_personalisation_available: getPersonalizationsFromProduct(product).length !== 0,
                personalisation_types: personalizationType,
                source_id: sourceID,
              },
              eventName: 'add_to_cart',
              products: mParticleProducts,
            })
          } else if (personalization) {
            otherEvent({
              eventName: 'pp_hcs_personalisation_added',
              attributes: {},
            })
          }

          return true
        } catch (errorType) {
          // If we got an error somewhere above, we'll let the error modal handle it. The modal will look at the error type and
          // think up a fitting response to the user, so we don't have to.
          openPopup(ErrorModal, {
            extraArguments: [product.name || ''],
            message: errorType.message,
            onHide: closeAllPopups,
          })

          if (!updateProduct) {
            otherEvent({
              eventName: 'cart_add_failed',
              attributes: {
                source_id: sourceID,
              },
            })
          }
          return false
        } finally {
          // handle any post processing
          await postProcess()
        }
      } catch {
        // We don't want to do anything here. The user just aborted the process.
        return false
      } finally {
        setInProgress(false)
      }
    },
    [
      additionalIDs,
      addToCart,
      askUserForSize,
      closeAllPopups,
      disableFlow,
      getPersonalizationData,
      isCrossSell,
      logProductAction,
      openPopup,
      otherEvent,
      postProcess,
      product,
      quantity,
      revalidate,
      sourceID,
      updateProduct,
      variantID,
    ],
  )

  return { callback, inProgress }
}
